/**
 * Storage helpers used by ajuda search history, favorites lists, alerts
 * preferences and the social resolver redirect glue. Centralizes
 * fallbacks (memory store) and TTL handling so the rest of the LAIS code
 * does not have to deal with quota / private-browsing edge cases.
 */
const memoryFallback = new Map();

function readBackend(scope) {
  if (typeof window === 'undefined') return null;
  if (scope === 'session') return window.sessionStorage;
  if (scope === 'local') return window.localStorage;
  return null;
}

function safeGet(scope, key) {
  const backend = readBackend(scope);
  try {
    if (backend) return backend.getItem(key);
  } catch (error) {
    /* fall through to memory */
  }
  return memoryFallback.get(`${scope}:${key}`) ?? null;
}

function safeSet(scope, key, value) {
  const backend = readBackend(scope);
  try {
    if (backend) {
      backend.setItem(key, value);
      return true;
    }
  } catch (error) {
    /* fall through to memory */
  }
  memoryFallback.set(`${scope}:${key}`, value);
  return true;
}

function safeDelete(scope, key) {
  const backend = readBackend(scope);
  try {
    if (backend) backend.removeItem(key);
  } catch (error) {
    /* ignore */
  }
  memoryFallback.delete(`${scope}:${key}`);
}

export function getJSON(key, { scope = 'local', fallback = null } = {}) {
  const raw = safeGet(scope, key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
}

export function setJSON(key, value, { scope = 'local', ttlSeconds = null } = {}) {
  const payload = ttlSeconds
    ? { value, expires_at: Math.floor(Date.now() / 1000) + ttlSeconds }
    : { value };
  safeSet(scope, key, JSON.stringify(payload));
}

export function readWithTTL(key, { scope = 'local', fallback = null } = {}) {
  const stored = getJSON(key, { scope, fallback: null });
  if (!stored) return fallback;
  if (stored.expires_at && stored.expires_at < Math.floor(Date.now() / 1000)) {
    safeDelete(scope, key);
    return fallback;
  }
  return stored.value ?? stored;
}

export function clearKey(key, { scope = 'local' } = {}) {
  safeDelete(scope, key);
}

export function pushHistory(key, entry, { limit = 10, scope = 'local' } = {}) {
  const current = getJSON(key, { scope, fallback: [] }) || [];
  const cleaned = [entry, ...current.filter(item => JSON.stringify(item) !== JSON.stringify(entry))].slice(0, limit);
  setJSON(key, cleaned, { scope });
  return cleaned;
}
